/**
 * RiskScoreModal — записване на готов резултат от модел за оценка на риска
 * (Gail / Tyrer-Cuzick / CanRisk / BRCAPRO). Приложението не изчислява тези
 * модели — само пази чуждия резултат и го взема предвид в плана за проверките.
 * Данни: GET/POST/DELETE /api/bhi/screening/risk-score
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, X, Loader2, Trash2, Paperclip, FileText } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

export default function RiskScoreModal({ open, onClose, onSaved }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const fileRef = useRef(null);

  const [data, setData] = useState(null);
  const [model, setModel] = useState("tyrer_cuzick");
  const [modelOther, setModelOther] = useState("");
  const [lifetime, setLifetime] = useState("");
  const [fiveYear, setFiveYear] = useState("");
  const [tenYear, setTenYear] = useState("");
  const [carrier, setCarrier] = useState("");
  const [assessedOn, setAssessedOn] = useState("");
  const [assessedBy, setAssessedBy] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/bhi/screening/risk-score`);
      setData(res);
    } catch { setData(null); }
  }, [API]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const models = data?.models || [];
  const picked = models.find((m) => m.value === model);

  const save = async () => {
    if (!assessedOn) { toast.error("Изберете дата на оценката"); return; }
    if (!lifetime && !fiveYear && !tenYear && !carrier) {
      toast.error("Въведете поне една стойност"); return;
    }
    if (model === "other" && !modelOther.trim()) { toast.error("Напишете името на модела"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("model", model);
      fd.append("assessed_on", assessedOn);
      if (lifetime) fd.append("lifetime_risk", lifetime);
      if (fiveYear) fd.append("five_year_risk", fiveYear);
      if (tenYear) fd.append("ten_year_risk", tenYear);
      if (carrier) fd.append("brca_carrier_prob", carrier);
      if (model === "other") fd.append("model_other", modelOther.trim());
      if (assessedBy) fd.append("assessed_by", assessedBy);
      if (note) fd.append("note", note);
      if (file) fd.append("attachment", file);
      const { data: res } = await axios.post(`${API}/bhi/screening/risk-score`, fd);
      setData((d) => ({ ...d, history: res.history, latest: res.history?.[0] || null }));
      setLifetime(""); setFiveYear(""); setTenYear(""); setCarrier(""); setNote(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Резултатът е записан — планът за проверките е обновен");
      onSaved?.(res.plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Записването не успя");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      const { data: res } = await axios.delete(`${API}/bhi/screening/risk-score/${id}`);
      setData((d) => ({ ...d, history: res.history, latest: res.history?.[0] || null }));
      onSaved?.(res.plan);
    } catch { toast.error("Изтриването не успя"); }
  };

  const openAttachment = async (id) => {
    try {
      const res = await axios.get(`${API}/bhi/screening/risk-score/${id}/attachment`,
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Файлът не може да бъде отворен"); }
  };

  const panel = isDark ? "bg-slate-900 border-slate-700 text-slate-100"
                       : "bg-white border-slate-200 text-slate-900";
  const field = `w-full px-2.5 py-2 rounded-lg text-[12px] border ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                    bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="risk-score-modal">
      <div className={`w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                       border shadow-2xl ${panel}`}>
        <div className={`sticky top-0 flex items-start justify-between gap-3 p-4 border-b ${
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Calculator className="w-5 h-5 text-rose-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-extrabold">Оценка на риска от модел</h2>
              <p className="text-[11px] opacity-70">
                Gail · Tyrer-Cuzick · CanRisk · BRCAPRO
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"
            data-testid="risk-score-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className={`p-3 rounded-xl text-[11px] leading-snug border-l-[3px]
                           border-cyan-500 ${isDark ? "bg-cyan-500/10" : "bg-cyan-50"}`}
            data-testid="risk-score-info">
            {data?.info_bg || "Ако лекар или генетик Ви е дал резултат от такъв модел, запишете го тук."}
            <div className="mt-1 font-semibold opacity-90">{data?.policy_bg}</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">Модел</span>
              <select value={model} onChange={(e) => setModel(e.target.value)}
                className={field} data-testid="risk-score-model">
                {models.map((m) => (
                  <option key={m.value} value={m.value}>{m.label_bg}</option>
                ))}
              </select>
              {picked?.hint_bg && (
                <span className="block text-[10px] mt-0.5 opacity-60">{picked.hint_bg}</span>
              )}
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">Дата на оценката</span>
              <input type="date" value={assessedOn} onChange={(e) => setAssessedOn(e.target.value)}
                className={field} data-testid="risk-score-date" />
            </label>
            {model === "other" && (
              <label className="block sm:col-span-2">
                <span className="text-[10px] font-bold uppercase opacity-60">Име на модела</span>
                <input value={modelOther} onChange={(e) => setModelOther(e.target.value)}
                  placeholder="например BCSC" className={field}
                  data-testid="risk-score-model-other" />
              </label>
            )}
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">
                Доживотен риск (%)
              </span>
              <input value={lifetime} onChange={(e) => setLifetime(e.target.value)}
                inputMode="decimal" placeholder="напр. 24.5" className={field}
                data-testid="risk-score-lifetime" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">
                5-годишен риск (%)
              </span>
              <input value={fiveYear} onChange={(e) => setFiveYear(e.target.value)}
                inputMode="decimal" placeholder="напр. 1.8" className={field}
                data-testid="risk-score-five-year" />
              <span className="block text-[10px] mt-0.5 opacity-60">
                над 1.67% — повод за разговор за лекарство за намаляване на риска
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">
                Риск за 10 години (%)
              </span>
              <input value={tenYear} onChange={(e) => setTenYear(e.target.value)}
                inputMode="decimal" placeholder="напр. 3.4" className={field}
                data-testid="risk-score-ten-year" />
              <span className="block text-[10px] mt-0.5 opacity-60">
                по NICE за възраст 40-50 г.: 3% — умерен, 8% — висок
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase opacity-60">
                Вероятност за носителство на BRCA1/2 (%)
              </span>
              <input value={carrier} onChange={(e) => setCarrier(e.target.value)}
                inputMode="decimal" placeholder="напр. 12" className={field}
                data-testid="risk-score-carrier" />
              <span className="block text-[10px] mt-0.5 opacity-60">
                10% и повече — насочване за генетичен тест (NICE)
              </span>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] font-bold uppercase opacity-60">
                Кой направи оценката
              </span>
              <input value={assessedBy} onChange={(e) => setAssessedBy(e.target.value)}
                placeholder="лекар, генетик, клиника" className={field}
                data-testid="risk-score-by" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[10px] font-bold uppercase opacity-60">Бележка</span>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="какво Ви е казано за резултата" className={field}
                data-testid="risk-score-note" />
            </label>
          </div>

          <label className={`flex items-center gap-2 p-3 rounded-xl border border-dashed
                             cursor-pointer text-[11px] ${
            isDark ? "border-slate-600 hover:bg-white/5" : "border-slate-300 hover:bg-slate-50"}`}
            data-testid="risk-score-file-label">
            <Paperclip className="w-4 h-4 text-rose-500 shrink-0" />
            <span className="flex-1 min-w-0 truncate">
              {file ? file.name : "Прикачи разпечатката (PDF или снимка) — по желание"}
            </span>
            <input ref={fileRef} type="file" className="hidden"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="risk-score-file" />
          </label>

          <button onClick={save} disabled={saving}
            className="w-full px-4 py-2.5 rounded-xl text-sm font-bold text-white
                       bg-gradient-to-r from-rose-500 to-pink-600 hover:opacity-95
                       disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="risk-score-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Запази резултата
          </button>

          {!!data?.history?.length && (
            <div className="space-y-2" data-testid="risk-score-history">
              <div className="text-[10px] font-bold uppercase opacity-60">Записани резултати</div>
              {data.history.map((h) => (
                <div key={h.id}
                  className={`p-3 rounded-xl border flex items-start gap-2 ${
                    isDark ? "border-white/10" : "border-slate-200"}`}
                  data-testid={`risk-score-item-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold">{h.model_label_bg}</div>
                    <div className="text-[11px] opacity-80">
                      {[
                        h.lifetime_risk != null ? `доживотен ${h.lifetime_risk}%` : null,
                        h.ten_year_risk != null ? `10 години ${h.ten_year_risk}%` : null,
                        h.five_year_risk != null ? `5 години ${h.five_year_risk}%` : null,
                        h.brca_carrier_prob != null
                          ? `носителство ${h.brca_carrier_prob}%` : null,
                      ].filter(Boolean).join(" · ")}
                    </div>
                    <div className="text-[10px] opacity-60">
                      {h.assessed_on}{h.assessed_by ? ` · ${h.assessed_by}` : ""}
                    </div>
                    {h.note && <div className="text-[10px] opacity-70 mt-0.5">{h.note}</div>}
                    {h.has_attachment && (
                      <button onClick={() => openAttachment(h.id)}
                        className="text-[10px] mt-1 inline-flex items-center gap-1 font-semibold
                                   text-cyan-500 hover:underline"
                        data-testid={`risk-score-attachment-${h.id}`}>
                        <FileText className="w-3 h-3" /> {h.file_name || "виж разпечатката"}
                      </button>
                    )}
                  </div>
                  <button onClick={() => remove(h.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 shrink-0"
                    data-testid={`risk-score-delete-${h.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
